import React, { useEffect, useMemo, useState } from 'react'
import {
  SmartList,
  IColumnElement,
  Icons,
  IHeaderItem,
  IconButton,
  SearchInput,
  PaginationPanel,
  Spinner,
  IRowElement,
  Container,
  Row,
} from '@doseme/cohesive-ui'
import { NAVY } from '@doseme/cohesive-ui'
import { faPlus } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { IEntityHashMap, isLoading } from 'flux-entities'

import { TProps } from './types'
import { TSSORealm } from '../../../../types/ssoRealm'
import { ssoRealmsEdit, SSO_REALMS_CREATE_URL } from '../../../../constants/urls/frontend'
import { DISPLAY_ITEMS_PER_PAGE } from '../../../../constants/display'
import { paginate } from '../../../utils'
import { smartListData } from '../../utils'
import { IInstallation } from '../../../../types/installation'
import { alphabetical } from '../../../../utils/arrayUtils'
import { ISSORealmsState } from '../../../../store/ssoRealms/types'

const cols: IHeaderItem[] = [
  {
    name: 'Realm',
  },
  {
    name: 'IDP Hint',
  },
  {
    name: 'Installation',
  },
  {
    name: 'Actions',
  }
]

const textContent = (ssoRealm: TSSORealm, allInstallations: IEntityHashMap<IInstallation>): string[] => [
  ssoRealm.realm,
  ssoRealm.idpHint,
  allInstallations[ssoRealm.installation] ? allInstallations[ssoRealm.installation].code : ''
]

const getSearchableContent = (
  ssoRealmIds: Array<number | string>,
  allSsoRealms: IEntityHashMap<TSSORealm>,
  allInstallations: IEntityHashMap<IInstallation>
) => ssoRealmIds.reduce<Record<number, string[]>>(
  (acc, x) => {
    return {
      ...acc,
      [x]: textContent(allSsoRealms[x], allInstallations)
    }
  }, {}
)

const getPaginatedListWithPageCount = (
  isPageLoading: boolean,
  ssoRealms: ISSORealmsState,
  searchContent: Record<number, string[]>,
  searchText: string,
  columnContent: (ssoRealm: TSSORealm) => IColumnElement[],
  currentPage: number,
  updateCurrentPage: (pages: number) => void
): { totalPages: number, paginatedData: IRowElement[] } => {
  let data: IRowElement[] = []

  if (isPageLoading) {
    data = smartListData({
      ids: ssoRealms.ids.sort(
        (x, y) => alphabetical(
          ssoRealms.all[x].realm,
          ssoRealms.all[y].realm
        )
      ),
      all: ssoRealms.all,
      searchContent,
      searchText,
      columnizeRow: columnContent
    })
  }

  const totalPages = Math.ceil(data.length / DISPLAY_ITEMS_PER_PAGE) || 1

  if (currentPage > totalPages) {
    updateCurrentPage(totalPages)
  }

  return {
    totalPages,
    paginatedData: paginate(data, { currentPage, itemsPerPage: DISPLAY_ITEMS_PER_PAGE })
  }
}

const Display: React.FC<TProps> = (props: TProps): JSX.Element | null => {
  const {
    ssoRealms,
    fetchSsoRealms,
    deleteSsoRealm,
    installations,
    fetchInstallations,
    history
  } = props

  useEffect(() => {
    if (!ssoRealms.touched) { fetchSsoRealms() }
  }, [ssoRealms.touched, fetchSsoRealms])

  useEffect(() => {
    if (!installations.touched) { fetchInstallations() }
  }, [installations.touched, fetchInstallations])

  const [currentPage, updateCurrentPage] = useState(1)
  const [searchText, setSearchText] = useState('')

  const searchableContent = useMemo(() => getSearchableContent(
    ssoRealms.ids,
    ssoRealms.all,
    installations.all
  ), [
    ssoRealms.ids,
    ssoRealms.all,
    installations.all
  ])

  if (!ssoRealms.touched || !installations.touched) {
    return null
  }

  const confirmDelete = (ssoRealm: TSSORealm) => {
    if (window.confirm('Are you sure you want to delete this SSO realm? This cannot be undone.')) {
      deleteSsoRealm(ssoRealm)
    }
  }

  const columnContent = (ssoRealm: TSSORealm): IColumnElement[] => [
    {
      name: 'realm',
      element: (
        <span>
          {ssoRealm.realm}
        </span>
      ),
      text: ssoRealm.realm,
    },

    {
      name: 'idpHint',
      element: (
        <span>
          {ssoRealm.idpHint}
        </span>
      ),
      text: ssoRealm.idpHint,
    },

    {
      name: 'installation',
      element: (
        <span>
          {installations.all[ssoRealm.installation].code}
        </span>
      ),
      text: installations.all[ssoRealm.installation].code
    },

    {
      name: 'actions',
      element: (
        <div className='d-flex align-items-center p-3'>
          <Icons.EditIconButton
            data-test='edit-button'
            onClick={() => history.push(ssoRealmsEdit(ssoRealm.id))}
          />
          <Icons.DeleteIconButton
            data-test='delete-button'
            onClick={() => confirmDelete(ssoRealm)}
          />
        </div>
      )
    }
  ]

  const isPageLoading = !isLoading(ssoRealms) && !isLoading(installations)

  const { totalPages, paginatedData } = getPaginatedListWithPageCount(
    isPageLoading,
    ssoRealms,
    searchableContent,
    searchText,
    columnContent,
    currentPage,
    updateCurrentPage,
  )

  return (
    <Container>
      <div className='d-flex justify-content-between my-2'>
        <div className='d-flex align-items-center'>
          <h4 className='co-h4 pr-2' data-test='sso-realms-list'>SSO Realms List</h4>
          {(!isLoading(ssoRealms) && !isLoading(installations))
            ? <IconButton
              onClick={() => history.push(SSO_REALMS_CREATE_URL)}
              size='30px'
            >
              <FontAwesomeIcon icon={faPlus} />
            </IconButton>
            : <Spinner color={NAVY} size='lg' />}
        </div>
        <SearchInput
          value={searchText}
          onChange={setSearchText}
        />
      </div>
      <Row>
        <SmartList
          loading={isLoading(ssoRealms) || isLoading(installations)}
          cols={cols}
          data={paginatedData}
          textIfEmpty={ssoRealms.ids.length ? 'No matching SSO Realms found.' : 'There are currently no SSO Realms.'}
        />
      </Row>
      <Row className='mt-2'>
        <PaginationPanel
          currentPage={currentPage}
          totalPages={totalPages}
          onPageChange={updateCurrentPage}
        />
      </Row>
    </Container >
  )
}

export {
  Display
}
